import { useEffect, useState } from 'react'
import { Avatar, Button, Dropdown, Layout, Tag, Tooltip } from 'antd'
import {
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/store/auth'
import { ROLES } from '@/shared/data/modules'
import { NotificationCenter } from './NotificationCenter'
import { GlobalSearch } from './GlobalSearch'

const { Header: AntHeader } = Layout

export function Header() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)
  const unit = useAuthStore((s) => s.unit)
  const logout = useAuthStore((s) => s.logout)
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      } else if (e.key === 'Escape') {
        setSearchOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const roleLabel = ROLES.find((r) => r.level === role)?.label ?? '—'

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: 'Hồ sơ cá nhân',
      },
      {
        key: 'switch-role',
        icon: <SwapOutlined />,
        label: 'Đổi vai trò / đơn vị',
        onClick: () => navigate('/select-role'),
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Đăng xuất',
        danger: true,
        onClick: () => {
          logout()
          navigate('/login')
        },
      },
    ],
  }

  return (
    <AntHeader className="!px-4 !flex items-center justify-between !bg-[var(--color-bg-surface)] border-b border-[var(--color-border-subtle)]">
      <div className="flex items-center gap-3">
        <Tag color="blue" className="!m-0 !text-[11px] !uppercase tracking-wider font-semibold">
          Prototype • Validate nghiệp vụ
        </Tag>
        <span className="text-[var(--color-text-muted)] text-xs hidden md:inline">
          Đề án Chuyển đổi số PK-KQ 2026–2030
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Tooltip title="Tìm kiếm (Ctrl+K)">
          <Button
            type="text"
            icon={<SearchOutlined />}
            onClick={() => setSearchOpen(true)}
            className="!text-[var(--color-text-secondary)]"
          >
            <span className="hidden md:inline ml-1 text-[11px] text-[var(--color-text-muted)] font-mono border border-[var(--color-border-subtle)] rounded px-1">⌘K</span>
          </Button>
        </Tooltip>

        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

        <Tooltip title="Trợ giúp">
          <Button
            type="text"
            icon={<QuestionCircleOutlined />}
            onClick={() => navigate('/help')}
            className="!text-[var(--color-text-secondary)]"
          />
        </Tooltip>

        <NotificationCenter />

        <div className="hidden md:flex flex-col items-end leading-tight mr-2 ml-3">
          <span className="text-[13px] text-[var(--color-text-primary)] font-medium">
            {unit?.name ?? '—'}
          </span>
          <span className="text-[11px] text-[var(--color-accent-400)]">
            {roleLabel}
          </span>
        </div>

        <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
          <button className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[var(--color-bg-elevated)] cursor-pointer transition">
            <Avatar
              size={32}
              style={{ backgroundColor: user?.avatarColor ?? '#1e6091' }}
            >
              {user?.fullName?.split(' ').slice(-1)[0]?.[0] ?? '?'}
            </Avatar>
            <div className="hidden md:flex flex-col items-start leading-tight">
              <span className="text-[13px] font-medium text-[var(--color-text-primary)]">
                {user?.fullName ?? 'Khách'}
              </span>
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {user?.rank ?? ''} {user?.position ? `• ${user.position}` : ''}
              </span>
            </div>
          </button>
        </Dropdown>
      </div>
    </AntHeader>
  )
}
